import { z } from 'zod/v4';

import {
    advancedBorderSettingsSchema,
    advancedRadiusSchema,
    buttonSizeSchema,
    buttonStyleTypeSchema,
    socialIconTypeSchema,
    socialNetworkSchema,
    themeButtonDataSchemaLegacy,
    themeButtonHoverSchemaLegacy,
} from '../../../common/schemas/legacy';

// The messenger/social-network id and icon-style enums now live in
// `common/schemas/legacy/social-network` (promoted when the social widget
// needed them); imported above and used in the bags below.

/**
 * Button rendering kind, mirroring `EButtonType`.
 */
export const chatBotButtonTypeSchema = z.enum(['iconText', 'icon', 'button']);

/**
 * The bot variable picked for a chat bot — `variableDataSchema` with every field nullable: adding a chat bot in
 * the form settings seeds them all `null` (since #56475), and that is what gets saved until one is picked.
 */
const chatBotVariableDataSchema = z.object({
    created_at: z.string().nullable(),
    bot_id: z.string().nullable(),
    _id: z.string().nullable(),
    name: z.string().nullable(),
    updated_at: z.string().nullable(),
    value: z.number().nullable(),
    value_type: z.number().nullable(),
});

/**
 * One chat-bot entry of the form widget's `chatBots` array — mirrors
 * `ChatBotData` from the chat-bot widget's types. String fields are nullable
 * (the host factory seeds them `null`/`''` and fills them as the user picks
 * a bot/chain).
 */
export const chatBotDataSchema = z.object({
    socialNetwork: socialNetworkSchema,
    type: chatBotButtonTypeSchema,
    iconType: socialIconTypeSchema,
    name: z.string().nullable(),
    link: z.string().nullable(),
    description: z.string().nullable(),
    botName: z.string().nullable(),
    botId: z.string().nullable(),
    variables: chatBotVariableDataSchema.nullable(),
    botChain: z.object({
        chainId: z.string().nullable(),
        name: z.string().nullable(),
        isDefault: z.boolean().nullable(),
    }),
    uri: z.string().nullish(),
    phone: z.string().nullish(),
    preventLinkConverting: z.boolean().nullish(),
});

/**
 * Styling bag for the chat-bot button group rendered under the form —
 * `ThemedBaseButton & ChatBotsButtonSettings` in the host types.
 */
export const chatBotsButtonSettingsSchema = z.object({
    // ThemedBaseButton side
    styleType: buttonStyleTypeSchema.nullable(),
    buttonBase: themeButtonDataSchemaLegacy,
    buttonActive: themeButtonDataSchemaLegacy.nullish(),
    buttonHover: themeButtonHoverSchemaLegacy.nullish(),
    // ChatBotsButtonSettings side
    type: chatBotButtonTypeSchema.nullable(),
    buttonSize: buttonSizeSchema.nullable(),
    iconType: socialIconTypeSchema.nullable(),
    isIndependentCorners: z.boolean().nullable(),
    advancedRadius: advancedRadiusSchema,
    advancedBorderSettings: advancedBorderSettingsSchema.nullish(),
});

export type FormChatBotData = z.infer<typeof chatBotDataSchema>;
export type ChatBotsButtonSettingsData = z.infer<typeof chatBotsButtonSettingsSchema>;
